import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import {
  formatCellAddress,
  parseCellRangeAddress,
  type ParsedCellRangeAddress,
} from "./excelCellAddress";
import { readRangeContent } from "./excelService";
import { executeModifyWorkbookTool } from "./executorWriteTools";
import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";
import { mapWithConcurrencyLimit } from "./executorWorkload";
import {
  canonicalizeCompanyName,
  companyNamesMatch,
  compareCompanyNames,
  type CompanyNameRelation,
} from "./companyNameMatch";
import { executeIdLookupToolDetailed } from "./idLookupTool";
import { ID_LOOKUP_UNRESOLVED_MARKER } from "./idLookupWorkload";
import {
  FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
  REAUTH_REQUIRED_OAUTH_ERROR_CODES,
} from "../shared/factset/factsetConstants";

export const COMPARE_COMPANY_NAME_COLUMNS_TOOL_NAME =
  "compare_company_name_columns";

const MAX_COMPARE_ROWS = 2_000;
const IDENTITY_LOOKUP_CONCURRENCY = 4;
const MAX_IDENTITY_LOOKUPS = 120;
const MAX_MISMATCH_SAMPLES = 15;
const MAX_REASON_NAME_CHARS = 120;

export const COMPARE_COMPANY_NAME_COLUMNS_TOOL: ChartCapabilityToolDef = {
  name: COMPARE_COMPANY_NAME_COLUMNS_TOOL_NAME,
  description:
    "Decide, for every row of a worksheet, whether a claimed company column names the " +
    "same company as a resolved company column, then write the verdict and a short " +
    "reason into an aligned two-column range. The comparison ignores accents, " +
    "punctuation, legal designators, country and annotation suffixes, acronyms, and " +
    "shortened names, and it asks FactSet whether the remaining differences are the " +
    "same company under another name. Use it instead of comparing worksheet company " +
    "names in your own reasoning: earlier range reads are summarized out of the " +
    "transcript, so hand-authored verdicts drift from the workbook.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        type: ["string", "null"],
        description:
          "Run step that owns the workbook writes, or null for a run-level fix.",
      },
      sheetName: {
        type: "string",
        description: "Worksheet holding all three ranges.",
      },
      claimedAddress: {
        type: "string",
        description:
          "One-column range of claimed company names, excluding its header (for example B2:B1012).",
      },
      resolvedAddress: {
        type: "string",
        description:
          "One-column range of resolved company names aligned to claimedAddress (for example E2:E1012).",
      },
      verdictAddress: {
        type: "string",
        description:
          "Two-column output range aligned to the same rows: verdict column then reason column (for example C2:D1012).",
      },
      matchLabel: {
        type: "string",
        description:
          "Verdict written when both columns name the same company, in the user's wording (for example correct).",
      },
      mismatchLabel: {
        type: "string",
        description:
          "Verdict written when the columns name different companies, in the user's wording (for example incorrect).",
      },
      unverifiableLabel: {
        type: ["string", "null"],
        description:
          `Verdict written when the resolved cell is blank. Defaults to "${ID_LOOKUP_UNRESOLVED_MARKER}".`,
      },
    },
    required: [
      "stepId",
      "sheetName",
      "claimedAddress",
      "resolvedAddress",
      "verdictAddress",
      "matchLabel",
      "mismatchLabel",
      "unverifiableLabel",
    ],
    additionalProperties: false,
  },
};

type VerdictKind =
  | "exact"
  | "variation"
  | "identity"
  | "mismatch"
  | "unverifiable";

interface ComparedRow {
  rowNumber: number;
  claimed: string;
  resolved: string;
  kind: VerdictKind;
  verdict: string;
  reason: string;
}

interface CompareInput {
  stepId: string | null;
  sheetName: string;
  claimedAddress: string;
  resolvedAddress: string;
  verdictAddress: string;
  matchLabel: string;
  mismatchLabel: string;
  unverifiableLabel: string;
}

class IdentityLookupReauthenticationError extends Error {
  constructor(readonly errorCode: string) {
    super(
      "FactSet authentication expired before the comparison was written. Reconnect FactSet and call the tool again.",
    );
    this.name = "IdentityLookupReauthenticationError";
    Object.setPrototypeOf(
      this,
      IdentityLookupReauthenticationError.prototype,
    );
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function readString(record: Record<string, unknown>, field: string): string {
  return typeof record[field] === "string" ? record[field].trim() : "";
}

function cellText(value: unknown): string {
  return typeof value === "string"
    ? value.trim()
    : value === null || value === undefined
      ? ""
      : String(value).trim();
}

/** True when a cell holds the lookup workload's unresolved marker. */
function isUnresolvedMarker(value: string): boolean {
  return (
    value.toLowerCase() === ID_LOOKUP_UNRESOLVED_MARKER.toLowerCase() ||
    value.startsWith("#")
  );
}

function shorten(value: string): string {
  return value.length > MAX_REASON_NAME_CHARS
    ? `${value.slice(0, MAX_REASON_NAME_CHARS - 1)}…`
    : value;
}

function failure(error: string, extra?: Record<string, unknown>): string {
  return JSON.stringify({ ok: false, error, ...extra });
}

function parseInput(rawInput: unknown): CompareInput | { error: string } {
  const raw = isRecord(rawInput) ? rawInput : {};
  const input: CompareInput = {
    stepId: typeof raw.stepId === "string" && raw.stepId.trim()
      ? raw.stepId.trim()
      : null,
    sheetName: readString(raw, "sheetName"),
    claimedAddress: readString(raw, "claimedAddress"),
    resolvedAddress: readString(raw, "resolvedAddress"),
    verdictAddress: readString(raw, "verdictAddress"),
    matchLabel: readString(raw, "matchLabel"),
    mismatchLabel: readString(raw, "mismatchLabel"),
    unverifiableLabel:
      readString(raw, "unverifiableLabel") || ID_LOOKUP_UNRESOLVED_MARKER,
  };
  if (!input.sheetName) return { error: "sheetName is required." };
  if (!input.matchLabel || !input.mismatchLabel) {
    return { error: "matchLabel and mismatchLabel are required." };
  }
  return input;
}

function rangeError(
  label: string,
  address: string,
  range: ParsedCellRangeAddress | undefined,
  expectedColumns: number,
): string | undefined {
  if (!range) return `${label} ${address} is not a valid A1 range.`;
  if (range.columnCount !== expectedColumns) {
    return `${label} ${address} must span exactly ${expectedColumns} column(s).`;
  }
  return undefined;
}

/** Reads one column of trimmed text keyed by worksheet row number. */
async function readColumn(
  sheetName: string,
  range: ParsedCellRangeAddress,
  workbookName: string | undefined,
): Promise<{ values: Map<number, string> } | { error: string }> {
  const readResult = await readRangeContent(
    sheetName,
    range.normalizedAddress,
    { includeFormulas: false, includeFormats: false },
    workbookName,
  );
  if (!readResult.range || readResult.truncated) {
    return {
      error: `${sheetName}!${range.normalizedAddress} could not be read completely.`,
    };
  }
  const read = readResult.range;
  const columnIndex = range.startColumn - (read.startColumn + 1);
  const values = new Map<number, string>();
  for (let rowNumber = range.startRow; rowNumber <= range.endRow; rowNumber += 1) {
    const rowIndex = rowNumber - (read.startRow + 1);
    values.set(rowNumber, cellText(read.values[rowIndex]?.[columnIndex]));
  }
  return { values };
}

/**
 * Collects the company names FactSet returns for one claimed name, so a row
 * whose columns disagree lexically can still be recognized as one company
 * that was renamed, rebranded, or is recorded under a local name.
 */
async function lookupIdentityNames(
  searchText: string,
  signal?: AbortSignal,
): Promise<{ names: string[] } | { failed: true }> {
  const result = await executeIdLookupToolDetailed(
    {
      search_text: searchText,
      filters: null,
      limit: FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
    },
    signal,
  );
  const errorCode = result.observationMetadata?.errorCode;
  if (
    typeof errorCode === "string" &&
    REAUTH_REQUIRED_OAUTH_ERROR_CODES.has(errorCode as never)
  ) {
    throw new IdentityLookupReauthenticationError(errorCode);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(result.output);
  } catch {
    return { failed: true };
  }
  if (!isRecord(parsed) || !Array.isArray(parsed.candidates)) {
    return { failed: true };
  }
  const names = new Set<string>();
  for (const candidate of parsed.candidates) {
    if (!isRecord(candidate)) continue;
    for (const field of ["name", "matched_name", "issuer_name"]) {
      const value = readString(candidate, field);
      if (value) names.add(value);
    }
  }
  return { names: Array.from(names) };
}

function groupConsecutiveRows(rows: readonly ComparedRow[]): ComparedRow[][] {
  const groups: ComparedRow[][] = [];
  for (const row of rows) {
    const current = groups[groups.length - 1];
    if (current && current[current.length - 1].rowNumber === row.rowNumber - 1) {
      current.push(row);
      continue;
    }
    groups.push([row]);
  }
  return groups;
}

function groupAddress(
  verdictRange: ParsedCellRangeAddress,
  group: readonly ComparedRow[],
): string | undefined {
  const start = formatCellAddress(verdictRange.startColumn, group[0].rowNumber);
  const end = formatCellAddress(
    verdictRange.endColumn,
    group[group.length - 1].rowNumber,
  );
  return start && end ? `${start}:${end}` : undefined;
}

/**
 * Compares a claimed company column against a resolved company column and
 * writes the verdict and reason itself, so the decision never depends on a
 * worksheet read that the model has since lost from its replayed transcript.
 */
export async function executeCompareCompanyNameColumnsTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  signal?: AbortSignal,
): Promise<string> {
  const input = parseInput(rawInput);
  if ("error" in input) return failure(input.error);

  const claimedRange = parseCellRangeAddress(input.claimedAddress);
  const resolvedRange = parseCellRangeAddress(input.resolvedAddress);
  const verdictRange = parseCellRangeAddress(input.verdictAddress);
  const shapeError =
    rangeError("claimedAddress", input.claimedAddress, claimedRange, 1) ??
    rangeError("resolvedAddress", input.resolvedAddress, resolvedRange, 1) ??
    rangeError("verdictAddress", input.verdictAddress, verdictRange, 2);
  if (shapeError) return failure(shapeError);
  if (!claimedRange || !resolvedRange || !verdictRange) {
    return failure("The requested ranges could not be parsed.");
  }
  if (
    claimedRange.startRow !== resolvedRange.startRow ||
    claimedRange.endRow !== resolvedRange.endRow ||
    claimedRange.startRow !== verdictRange.startRow ||
    claimedRange.endRow !== verdictRange.endRow
  ) {
    return failure(
      "claimedAddress, resolvedAddress, and verdictAddress must cover the same rows.",
    );
  }
  if (claimedRange.rowCount > MAX_COMPARE_ROWS) {
    return failure(
      `The comparison supports at most ${MAX_COMPARE_ROWS} rows per call; split the range.`,
    );
  }

  const workbookName = ctx.session.pinnedWorkbookName;
  const claimedColumn = await readColumn(
    input.sheetName,
    claimedRange,
    workbookName,
  );
  if ("error" in claimedColumn) return failure(claimedColumn.error);
  const resolvedColumn = await readColumn(
    input.sheetName,
    resolvedRange,
    workbookName,
  );
  if ("error" in resolvedColumn) return failure(resolvedColumn.error);

  const rows: ComparedRow[] = [];
  const lexicalMismatches: ComparedRow[] = [];
  for (
    let rowNumber = claimedRange.startRow;
    rowNumber <= claimedRange.endRow;
    rowNumber += 1
  ) {
    const claimed = claimedColumn.values.get(rowNumber) ?? "";
    if (!claimed) continue;
    const resolved = resolvedColumn.values.get(rowNumber) ?? "";
    if (!resolved || isUnresolvedMarker(resolved)) {
      rows.push({
        rowNumber,
        claimed,
        resolved,
        kind: "unverifiable",
        verdict: input.unverifiableLabel,
        reason: "No resolved company name is present for this row.",
      });
      continue;
    }
    const relation: CompanyNameRelation = compareCompanyNames(
      claimed,
      resolved,
    ).relation;
    if (relation === "different") {
      const row: ComparedRow = {
        rowNumber,
        claimed,
        resolved,
        kind: "mismatch",
        verdict: input.mismatchLabel,
        reason: `Names a different company than ${shorten(resolved)}.`,
      };
      rows.push(row);
      lexicalMismatches.push(row);
      continue;
    }
    rows.push({
      rowNumber,
      claimed,
      resolved,
      kind: relation,
      verdict: input.matchLabel,
      reason:
        relation === "exact"
          ? `Matches ${shorten(resolved)}.`
          : `Same company as ${shorten(resolved)}, written differently.`,
    });
  }

  if (rows.length === 0) {
    return failure(
      `No claimed company names were found in ${input.sheetName}!${claimedRange.normalizedAddress}.`,
    );
  }

  const identityCache = new Map<string, string[]>();
  const identityCounts = {
    namesChecked: 0,
    namesFailed: 0,
    namesSkipped: 0,
    rowsMatched: 0,
  };
  const distinctMismatchNames: string[] = [];
  const mismatchRowsByName = new Map<string, ComparedRow[]>();
  for (const row of lexicalMismatches) {
    const key = canonicalizeCompanyName(row.claimed).key || row.claimed.toLowerCase();
    const existing = mismatchRowsByName.get(key);
    if (existing) {
      existing.push(row);
      continue;
    }
    mismatchRowsByName.set(key, [row]);
    distinctMismatchNames.push(key);
  }
  const checkedNames = distinctMismatchNames.slice(0, MAX_IDENTITY_LOOKUPS);
  identityCounts.namesSkipped =
    distinctMismatchNames.length - checkedNames.length;

  try {
    const lookups = await mapWithConcurrencyLimit(
      checkedNames,
      IDENTITY_LOOKUP_CONCURRENCY,
      async (key) => {
        const searchText = mismatchRowsByName.get(key)?.[0]?.claimed ?? key;
        const result = await lookupIdentityNames(searchText, signal);
        return { key, result };
      },
    );
    for (const { key, result } of lookups) {
      if ("failed" in result) {
        identityCounts.namesFailed += 1;
        continue;
      }
      identityCounts.namesChecked += 1;
      identityCache.set(key, result.names);
    }
  } catch (error) {
    if (error instanceof IdentityLookupReauthenticationError) {
      return failure(error.message, {
        errorCode: error.errorCode,
        reauthenticationRequired: true,
        rowsWritten: 0,
      });
    }
    throw error;
  }

  mismatchRowsByName.forEach((mismatchRows, key) => {
    const names = identityCache.get(key);
    if (!names) {
      for (const row of mismatchRows) {
        row.reason = `${row.reason} FactSet identity check unavailable.`;
      }
      return;
    }
    for (const row of mismatchRows) {
      const identityName = names.find((name) =>
        companyNamesMatch(name, row.resolved),
      );
      if (!identityName) continue;
      row.kind = "identity";
      row.verdict = input.matchLabel;
      row.reason = `Same company as ${shorten(row.resolved)}; FactSet lists this name as ${shorten(identityName)}.`;
      identityCounts.rowsMatched += 1;
    }
  });

  const groups = groupConsecutiveRows(rows);
  const ranges: Array<{
    address: string;
    action: "fill";
    values: string[][];
  }> = [];
  for (const group of groups) {
    const address = groupAddress(verdictRange, group);
    if (!address) {
      return failure(
        `A verdict range could not be formed for rows ${group[0].rowNumber}-${group[group.length - 1].rowNumber}.`,
      );
    }
    ranges.push({
      address,
      action: "fill",
      values: group.map((row) => [row.verdict, row.reason]),
    });
  }

  const applyOutput = await executeModifyWorkbookTool(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [{ sheetName: input.sheetName, ranges }],
      message: `Wrote company-name comparison verdicts for rows ${rows[0].rowNumber}-${rows[rows.length - 1].rowNumber}`,
    },
  });
  let applyResult: unknown;
  try {
    applyResult = JSON.parse(applyOutput);
  } catch {
    applyResult = applyOutput;
  }
  if (
    !isRecord(applyResult) ||
    applyResult.ok !== true ||
    applyResult.applied !== true
  ) {
    return failure("The comparison verdicts were not applied.", {
      applyResult,
    });
  }

  const verificationErrors: string[] = [];
  for (const group of groups) {
    const address = groupAddress(verdictRange, group);
    if (!address) continue;
    const readResult = await readRangeContent(
      input.sheetName,
      address,
      { includeFormulas: false, includeFormats: false },
      workbookName,
    );
    if (!readResult.range || readResult.truncated) {
      verificationErrors.push(
        `${input.sheetName}!${address} could not be read back completely.`,
      );
      continue;
    }
    const read = readResult.range;
    const columnIndex = verdictRange.startColumn - (read.startColumn + 1);
    for (const row of group) {
      const rowIndex = row.rowNumber - (read.startRow + 1);
      const actual = read.values[rowIndex] ?? [];
      if (
        cellText(actual[columnIndex]) !== row.verdict ||
        cellText(actual[columnIndex + 1]) !== row.reason
      ) {
        verificationErrors.push(
          `${input.sheetName}!${formatCellAddress(verdictRange.startColumn, row.rowNumber) ?? address} did not match the written verdict.`,
        );
      }
    }
  }

  const counts: Record<VerdictKind, number> = {
    exact: 0,
    variation: 0,
    identity: 0,
    mismatch: 0,
    unverifiable: 0,
  };
  for (const row of rows) counts[row.kind] += 1;

  if (verificationErrors.length > 0) {
    return failure("Comparison verdict read-back verification failed.", {
      applied: true,
      verificationErrors: verificationErrors.slice(0, 10),
      retryWith: COMPARE_COMPANY_NAME_COLUMNS_TOOL_NAME,
    });
  }

  const remainingIdentityRows = rows.filter(
    (row) => row.kind === "mismatch" && !identityCache.has(
      canonicalizeCompanyName(row.claimed).key || row.claimed.toLowerCase(),
    ),
  );
  return JSON.stringify({
    ok: true,
    applied: true,
    sheetName: input.sheetName,
    verdictAddress: verdictRange.normalizedAddress,
    rowsCompared: rows.length,
    rowsSkippedBlank: claimedRange.rowCount - rows.length,
    counts,
    identityChecks: identityCounts,
    mismatchSamples: rows
      .filter((row) => row.kind === "mismatch")
      .slice(0, MAX_MISMATCH_SAMPLES)
      .map((row) => ({
        row: row.rowNumber,
        claimed: row.claimed,
        resolved: row.resolved,
      })),
    nextAction:
      identityCounts.namesSkipped > 0 && remainingIdentityRows.length > 0
        ? `Verdicts are written for every row. ${identityCounts.namesSkipped} differing names exceeded the per-call FactSet identity budget; call ${COMPARE_COMPANY_NAME_COLUMNS_TOOL_NAME} again for rows ${remainingIdentityRows[0].rowNumber}-${remainingIdentityRows[remainingIdentityRows.length - 1].rowNumber} to check them.`
        : "Every row now holds a verdict and reason written from the workbook itself. Report the counts and finish the step.",
  });
}
